import {
  type ChangeEvent,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useEdges, useReactFlow } from 'reactflow';
import '../../assets/styles/animatedBorder.scss';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { NodeProps } from '../../util/Types/NodeProps';

import { Box, Typography } from '@mui/material';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useSocketStore } from '../../store/storeSocket';
import { mcpSourceTypes } from '../../types/mcpNode';
import { isActiveEdge } from '../../util/findActiveHandle';
import { replacePlaceholders } from '../../util/myFunctionsCoreDisplay';
import { useObserveElementWidth } from '../../util/useObserveElement';
import { getProvider } from '../ThirdPartyIntegration/registry';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';
import McpCommandPanel from './mcp/McpCommandPanel';
import McpConnectionPanel from './mcp/McpConnectionPanel';
import McpToolPanel from './mcp/McpToolPanel';
import { useMcpFieldDebounce } from './mcp/hooks/useMcpFieldDebounce';
import { useMcpListToolsCatalogQuery } from './mcp/hooks/useMcpListToolsCatalogQuery';
import { useMcpNodePersist } from './mcp/hooks/useMcpNodePersist';
import { useMcpNodeState } from './mcp/hooks/useMcpNodeState';
import { useMcpSocketResponse } from './mcp/hooks/useMcpSocketResponse';
import { useMcpSubmit } from './mcp/hooks/useMcpSubmit';
import { MCP_INPUT_COLUMN_MAX_PX } from './mcp/mcpNodeUiConstants';

export const sourceTypes = mcpSourceTypes;

function McpServer({ id, data, type }: NodeProps) {
  const edges = useEdges();
  const { activeNodeSockets, setActiveNodeSockets, socket } = useSocketStore(
    state => state,
  );

  const { user } = useAuthStore(state => state);
  const organizationId =
    user?.selectedCompanyId ??
    (user as { lastCompanyLogin?: { id: string } } | null)?.lastCompanyLogin
      ?.id ??
    '';

  const nodeData = (data ?? {}) as Record<string, unknown>;
  const { formData, setFormData, markUserEdited } = useMcpNodeState(
    data as Record<string, unknown> | undefined,
  );

  const rawMcpServerUrl =
    data?.serverUrlData?.length && data.serverUrlData.length > 0
      ? data.serverUrlData.map((item: { text: string }) => item.text).join('')
      : formData.serverUrl;

  const setNotification = useNotificationStore(state => state.setNotification);

  const mcpResponseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const clearMcpResponseTimeout = useCallback(() => {
    if (mcpResponseTimeoutRef.current) {
      clearTimeout(mcpResponseTimeoutRef.current);
      mcpResponseTimeoutRef.current = null;
    }
  }, []);

  const mcpSubmitUiRef = useRef<{
    setIsLoading: (v: boolean) => void;
    setHasToExecute: (v: boolean) => void;
  }>({
    setIsLoading: () => {},
    setHasToExecute: () => {},
  });

  const { setNodes: setNodeState } = useReactFlow();
  const { setSchema, setSaving, flowId, schema } = useFlowsStore(
    state => state,
  );

  const onSave = useMcpNodePersist(
    edges,
    id,
    flowId,
    nodeData,
    setNodeState as (value: unknown) => void,
    setSaving,
    setSchema,
  );

  const { onChange } = useMcpFieldDebounce(
    edges,
    id,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
    markUserEdited,
    setFormData,
  );

  const { pendingClientRequestIdRef } = useMcpSocketResponse(
    id,
    useCallback(
      (parsed: { text: string; isError: boolean; errorMessage?: string }) => {
        clearMcpResponseTimeout();
        onSave({ text: parsed.text }, 'execute');
        setActiveNodeSockets((prev: string[]) =>
          prev.filter((item: string) => item !== id),
        );
        mcpSubmitUiRef.current.setIsLoading(false);
        mcpSubmitUiRef.current.setHasToExecute(true);
        setFormData(prev => ({ ...prev, text: parsed.text }));
        if (parsed.isError) {
          setNotification({
            message: parsed.errorMessage ?? 'MCP call failed',
            type: NotificationType.Error,
          });
        }
      },
      [
        clearMcpResponseTimeout,
        id,
        onSave,
        setActiveNodeSockets,
        setFormData,
        setNotification,
      ],
    ),
    clearMcpResponseTimeout,
  );

  const resolvedServerUrl = useMemo(
    () => replacePlaceholders(rawMcpServerUrl, schema)?.trim() ?? '',
    [rawMcpServerUrl, schema],
  );
  const debouncedServerUrl = useDebouncedValue(resolvedServerUrl, 800);
  const resolvedAuthToken = useMemo(
    () => replacePlaceholders(formData.authToken, schema)?.trim() ?? '',
    [formData.authToken, schema],
  );
  const debouncedAuthToken = useDebouncedValue(resolvedAuthToken, 800);

  const listToolsInput = useMemo(() => {
    const savedId = formData.integrationId?.trim();
    if (savedId && organizationId) {
      return {
        type: 'integration' as const,
        integrationId: savedId,
        organizationId,
      };
    }
    const u = debouncedServerUrl;
    if (u.startsWith('http://') || u.startsWith('https://')) {
      return {
        type: 'url' as const,
        serverUrl: u,
        authToken: debouncedAuthToken || undefined,
      };
    }
    return { type: 'none' as const };
  }, [
    formData.integrationId,
    organizationId,
    debouncedServerUrl,
    debouncedAuthToken,
  ]);

  const toolsQuery = useMcpListToolsCatalogQuery(listToolsInput);
  const availableTools = toolsQuery.data ?? [];
  const isLoadingTools = toolsQuery.isFetching;

  const userObjects = useMemo(
    () =>
      [...(user?.spaceObjects || []), ...(user?.objects || [])] as {
        id: string;
        text: { text: string; id: string }[];
        name: string;
      }[],
    [user?.spaceObjects, user?.objects],
  );

  const { handleCallTool, isLoading, setIsLoading, setHasToExecute } =
    useMcpSubmit({
      id,
      data: data as Record<string, unknown> | undefined,
      edges,
      flowId,
      schema,
      organizationId,
      rawMcpServerUrl,
      formData,
      availableTools,
      socket,
      setActiveNodeSockets,
      pendingClientRequestIdRef,
      mcpResponseTimeoutRef,
      clearMcpResponseTimeout,
      setNodeState,
      setSaving,
      setSchema,
      setFormData,
      setNotification,
      onSave,
      userObjects,
      activeNodeSockets,
    });

  useLayoutEffect(() => {
    mcpSubmitUiRef.current = { setIsLoading, setHasToExecute };
  }, [setIsLoading, setHasToExecute]);

  const handleRun = (run: string) => {
    onSave({ run });
  };

  const onChangeOnExecute = (e: ChangeEvent<HTMLInputElement>) => {
    onSave({ clearOnExecution: e.target.checked });
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType?.colors as Record<string, string> | undefined;

  const savedHttpIntegrationSelected = Boolean(formData.integrationId?.trim());
  const serverUrlTargetActive = isActiveEdge(edges, id, 'serverUrl', 'target');
  const authTokenTargetActive = isActiveEdge(edges, id, 'authToken', 'target');

  const showServerUrlField = !organizationId || serverUrlTargetActive;

  const showAuthTokenField = !organizationId || authTokenTargetActive;

  const { height: maxOutputHeight, ref: inputRef } =
    useObserveElementWidth<HTMLDivElement>();

  const canLoadTools =
    Boolean(formData.integrationId?.trim() && organizationId) ||
    Boolean(rawMcpServerUrl.trim());

  return (
    <>
      <Box className={isLoading ? 'node' : ''}>
        <Node
          id={id}
          edges={edges}
          type={type}
          data={data}
          isLoading={isLoading}
          btnText='Call Tool'
          onSubmit={handleCallTool}
          handleRun={handleRun}
          showTokensUsed={false}
        >
          <Box display='flex' alignItems='stretch' flexWrap='nowrap'>
            <Box
              sx={{
                flex: '0 0 auto',
                width: '100%',
                maxWidth: {
                  xs: '100%',
                  sm: MCP_INPUT_COLUMN_MAX_PX,
                },
                minWidth: { xs: 260, sm: 280 },
              }}
            >
              <Box
                p='16px'
                borderRight={`1px solid ${
                  user?.theme === 'dark' ? '#475467' : '#EAECF0'
                }`}
                ref={inputRef}
              >
                <Box>
                  <McpConnectionPanel
                    user={user}
                    organizationId={organizationId}
                    nodeId={id}
                    edges={edges}
                    data={nodeData}
                    formData={formData}
                    mcpProviderConfig={getProvider('mcp') ?? undefined}
                    showServerUrlField={showServerUrlField}
                    showAuthTokenField={showAuthTokenField}
                    savedHttpIntegrationSelected={savedHttpIntegrationSelected}
                    onChange={onChange}
                    onIntegrationSelected={integrationId => {
                      setFormData(prev => ({ ...prev, integrationId }));
                      onSave({ integrationId });
                    }}
                    onIntegrationDisconnected={() => {
                      setFormData(prev => ({ ...prev, integrationId: '' }));
                      onSave({ integrationId: '' });
                    }}
                    findColor={findColor}
                  />
                  <McpToolPanel
                    user={user}
                    nodeId={id}
                    edges={edges}
                    data={nodeData}
                    formData={formData}
                    availableTools={availableTools}
                    isLoadingTools={isLoadingTools}
                    rawMcpServerUrl={rawMcpServerUrl}
                    organizationId={organizationId}
                    canLoadTools={canLoadTools}
                    onLoadTools={() => {
                      void toolsQuery
                        .refetch()
                        .then(r => {
                          const n = r.data?.length ?? 0;
                          setNotification({
                            message:
                              n > 0
                                ? `Loaded ${n} tool(s) from server`
                                : 'No tools returned from server',
                            type:
                              n > 0
                                ? NotificationType.Success
                                : NotificationType.Warning,
                          });
                        })
                        .catch(err => {
                          setNotification({
                            message: getAPIErrorMessage(err),
                            type: NotificationType.Error,
                          });
                        });
                    }}
                    onToolSelect={name => {
                      setFormData(prev => ({ ...prev, toolName: name }));
                      if (!name.trim()) {
                        onSave({ toolName: '', toolContext: undefined });
                        return;
                      }
                      const selected = availableTools.find(t => t.name === name);
                      onSave({
                        toolName: name,
                        toolContext: selected
                          ? {
                              name: selected.name,
                              description: selected.description,
                              inputSchema: selected.inputSchema,
                            }
                          : undefined,
                      });
                    }}
                    onChange={onChange}
                    findColor={findColor}
                  />
                  <McpCommandPanel
                    user={user}
                    nodeId={id}
                    edges={edges}
                    data={nodeData}
                    formData={formData}
                    availableTools={availableTools}
                    onChange={onChange}
                    findColor={findColor}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              p='16px 24px 16px 16px'
              display='flex'
              flexDirection='column'
              sx={{
                flex: '1 1 320px',
                minWidth: { xs: 260, sm: 300 },
                overflow: 'hidden',
              }}
            >
              <Typography
                variant='caption'
                fontWeight={700}
                color='text.secondary'
                sx={{ display: 'block', mb: 1.5 }}
              >
                Output
              </Typography>
              <OutputTextarea
                maxOutputHeight={maxOutputHeight}
                previewIndex={data.previewIndex}
                value={data.text}
                activeHandle={isActiveEdge(edges, id, 'output', 'source')}
                placement={data?.placement}
                labelName='Output'
                nodeLabel={data?.label}
                type={data.myId}
                nodeId={id}
                collapsed={data?.collapsed || false}
                findColor={findColor}
                hasClearOnExecute={true}
                onChangeOnExecute={onChangeOnExecute}
                clearOnExecution={data.clearOnExecution || false}
              />
            </Box>
          </Box>
        </Node>
      </Box>
    </>
  );
}

export default memo(McpServer);
